import React from 'react';
import { motion } from 'framer-motion';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from '@nextui-org/react';
import { FiArrowRight, FiAward, FiDownload } from 'react-icons/fi';

import pro from './pic4.jpg';
import { ACHIEVEMENTS, RESUME_URL } from '../data/site';

const ROLES = [
  'Software Development Intern @ Clink AI',
  'React Native + Expo Developer',
  'Next.js / React Engineer',
  'Full-Stack Web Developer',
];

const STATS = [
  { num: '15+', label: 'Projects Shipped' },
  { num: '1850+', label: 'LeetCode Rating' },
  { num: '2', label: 'Apps in Production' },
];

function useTypewriter(words, typeMs = 65, deleteMs = 32, holdMs = 1700) {
  const [index, setIndex] = React.useState(0);
  const [text, setText] = React.useState('');
  const [deleting, setDeleting] = React.useState(false);

  React.useEffect(() => {
    const word = words[index % words.length];

    if (!deleting && text === word) {
      const t = setTimeout(() => setDeleting(true), holdMs);
      return () => clearTimeout(t);
    }

    if (deleting && text === '') {
      setDeleting(false);
      setIndex((i) => (i + 1) % words.length);
      return undefined;
    }

    const t = setTimeout(
      () =>
        setText((prev) =>
          deleting ? word.slice(0, prev.length - 1) : word.slice(0, prev.length + 1)
        ),
      deleting ? deleteMs : typeMs
    );
    return () => clearTimeout(t);
  }, [text, deleting, index, words, typeMs, deleteMs, holdMs]);

  return text;
}

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
};

const item = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};

export default function Head() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const typed = useTypewriter(ROLES);

  return (
    <header className="hero" id="home">
      <div className="hero-grid">
        <motion.div variants={container} initial="hidden" animate="show">
          <motion.div variants={item} className="hero-badge">
            <span className="hero-dot" />
            Open to SDE roles &middot; Bangalore / Remote
          </motion.div>

          <motion.h1 variants={item} className="hero-title">
            Hi, I&apos;m <span className="grad">Harshit Pandey</span>
          </motion.h1>

          <motion.p variants={item} className="hero-role">
            {typed}
            <span className="hero-cursor" />
          </motion.p>

          <motion.p variants={item} className="hero-desc">
            I build <b>production mobile and web apps</b> &mdash; shipping React Native
            (Expo) screens with 60fps Reanimated transitions, interactive MapLibre GL
            maps, and <b>Next.js</b> dashboards on React Server Components. Strong
            foundation in <b>DSA</b>, with a focus on performance and clean, scalable
            architecture.
          </motion.p>

          <motion.div variants={item} className="hero-actions">
            <a
              href={RESUME_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary-x"
            >
              <FiDownload /> View Resume
            </a>

            <button type="button" className="btn-ghost-x" onClick={onOpen}>
              <FiAward /> Achievements
            </button>

            <a href="#project" className="btn-ghost-x">
              See Projects <FiArrowRight />
            </a>
          </motion.div>

          <motion.div variants={item} className="hero-stats">
            {STATS.map((s) => (
              <div key={s.label}>
                <div className="hero-stat-num">{s.num}</div>
                <div className="hero-stat-label">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          className="hero-portrait"
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.85, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          <img src={pro} alt="Harshit Pandey" />
          <div className="hero-float f1">React Native</div>
          <div className="hero-float f2">Next.js &middot; RSC</div>
        </motion.div>
      </div>

      <Modal
        backdrop="blur"
        isOpen={isOpen}
        onClose={onClose}
        size="2xl"
        scrollBehavior="inside"
        classNames={{ base: 'modal-x' }}
      >
        <ModalContent>
          {(close) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Achievements</ModalHeader>
              <ModalBody>
                {ACHIEVEMENTS.map((a) => (
                  <div key={a.text} className="modal-line">
                    <span className="modal-bullet">&#9656;</span>
                    <span>
                      {a.text}{' '}
                      <a href={a.href} target="_blank" rel="noopener noreferrer">
                        {a.label} &#8599;
                      </a>
                    </span>
                  </div>
                ))}
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={close}>
                  Close
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </header>
  );
}
